import { transformUnits } from '@/helpers/unit-formatter/unit-formatter';
import { FormattedUnitLabels, UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import { unitValueToFormattedUnitLabels } from '@/helpers/unit-formatter/unit-formatter.utils';
import { isNullOrUndefined } from '@/utils/is-null-or-undefined';

import { SUPPORTED_UNITS } from '../unit-formatter.constants';

/**
 * Formatter for boolean values.
 * Transforms boolean values to string representations based on displayUnits:
 * - 'on-off': true → 'ON', false → 'OFF'
 * - 'open-closed': true → 'OPEN', false → 'CLOSED'
 * - 'string': true → 'TRUE', false → 'FALSE'
 * - default: returns original boolean value as string
 *
 * @param {UnitValue} unitValue - The boolean value and units to format
 * @param {boolean} unitValue.value - The boolean value to format
 * @param {string} [unitValue.units] - The current units (typically undefined or 'boolean')
 * @param {string} [displayUnits] - Optional target units to transform to ('on-off', 'open-closed', 'string')
 * @returns {FormattedUnitLabels} Formatted labels containing:
 *   - valueLabel: The transformed boolean value as a string
 *   - unitsLabel: Empty string (transformed values have no units)
 *   - valueUnitsLabel: The transformed value string
 *
 * @example
 * formatBoolean({ value: true, units: undefined }, 'on-off')
 * // Returns: { valueLabel: 'ON', unitsLabel: '', valueUnitsLabel: 'ON' }
 *
 * @example
 * formatBoolean({ value: false, units: undefined }, 'open-closed')
 * // Returns: { valueLabel: 'CLOSED', unitsLabel: '', valueUnitsLabel: 'CLOSED' }
 *
 * @example
 * formatBoolean({ value: true, units: undefined }, 'string')
 * // Returns: { valueLabel: 'TRUE', unitsLabel: '', valueUnitsLabel: 'TRUE' }
 */
export const formatBoolean = (unitValue: UnitValue, displayUnits?: string): FormattedUnitLabels => {
  // If the units is undefined, empty, or none and the value is of type boolean, set the units to boolean
  // This is to ensure that the boolean transformer is run
  if (
    (isNullOrUndefined(unitValue.units) ||
      unitValue.units === '' ||
      unitValue.units === SUPPORTED_UNITS.NONE) &&
    typeof unitValue.value === 'boolean'
  ) {
    unitValue.units = SUPPORTED_UNITS.BOOLEAN;
  }

  const transformed = transformUnits(unitValue, displayUnits);

  return unitValueToFormattedUnitLabels({
    value: transformed.value,
    units: transformed.units,
  });
};
